import { useSvgIcon } from '@/hooks/useSvg';
import { BRAND_PRIMARY } from '@/config/theme';
import React, { useMemo } from 'react';

export interface SvgIconProps {
  name: string;
  className?: string;
  size?: number;
  color?: string;
  onClick?: () => void;
  fallback?: React.ReactNode;
}

const SVG_PRESERVED_FILLS = new Set(
  ['none', 'currentColor', 'white', '#fff', '#ffffff', BRAND_PRIMARY].map((c) => c.toLowerCase()),
);

const SVG_PRESERVED_STROKES = new Set(
  ['none', 'currentColor', 'white', '#fff', '#ffffff'].map((c) => c.toLowerCase()),
);

const processSvgColors = (svgContent: string) =>
  svgContent
    .replace(/fill="([^"]*)"/gi, (match, value: string) =>
      SVG_PRESERVED_FILLS.has(value.toLowerCase()) ? match : 'fill="currentColor"',
    )
    .replace(/stroke="([^"]*)"/gi, (match, value: string) =>
      SVG_PRESERVED_STROKES.has(value.toLowerCase()) ? match : 'stroke="currentColor"',
    );

export const SvgIcon: React.FC<SvgIconProps> = ({
  name,
  className = '',
  size = 20,
  color = 'currentColor',
  onClick,
  fallback,
}) => {
  const { svgContent, isLoaded, error } = useSvgIcon(name);

  const processedSvg = useMemo(
    () => (svgContent ? processSvgColors(svgContent) : null),
    [svgContent],
  );

  if (!isLoaded) {
    return (
      <div
        className={`bg-gray-200 animate-pulse rounded ${className}`}
        style={{ width: size, height: size }}
      >
        {fallback}
      </div>
    );
  }

  if (error || !processedSvg) {
    return (
      <div
        className={`bg-gray-300 rounded flex items-center justify-center ${className}`}
        style={{ width: size, height: size }}
      >
        {fallback || <span style={{ fontSize: size * 0.6 }}>❌</span>}
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        width: size,
        height: size,
        color: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClick}
      dangerouslySetInnerHTML={{ __html: processedSvg }}
    />
  );
};
